import React from "react";
import { useSelector } from "react-redux";
import { IoIosArrowBack } from "react-icons/io";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import AddJobAllJobsProfile from "../../StylesWrappers/General/AddJobAllJobsProfile";
import { RootState } from "../../store";
import Loader from "../General/Loader";
import { dateAndTime } from "../../utils/date";
import { convertNewLines } from "../../utils/convertNewLines";
import { LinkBtn } from "../General";

interface SinglePostJobProps {
  loaderContainer?: string;
}

const SinglePostJobComp = ({ loaderContainer }: SinglePostJobProps) => {
  const { isSinglePostJobLoading, singleJobFetched } = useSelector(
    (store: RootState) => store.singlePostJobStore
  );

  const navigate = useNavigate();

  const {
    position,
    company,
    benefits,
    createdAt,
    interviewProcess,
    jobDescription,
    jobEmail,
    jobLocation,
    jobRequirements,
    jobType,
    jobUrl,
    qualifications,
    socialMedia,
  } = singleJobFetched;

  // const { whatsapp, telegram } = socialMedia;

  // Get the value after the https
  const httpsValue = (value: string) => {
    const arraylize = value?.split("https://");
    if (arraylize) {
      return arraylize[1];
    }
    return value;
  };

  const handleArrowBack = () => {
    navigate(-1);
  };

  // Function to convert newlines to <br> tags
  const convertNewLinesToBreak = (text: string) => {
    return convertNewLines(text);
  };

  if (isSinglePostJobLoading) {
    return (
      <LoaderWrapper>
        <Loader loaderStyle={`wide__loader ${loaderContainer}`} />
      </LoaderWrapper>
    );
  }

  return (
    <AddJobAllJobsProfile>
      <JobDetailWrapper>
        <IoIosArrowBack className="arrow__back" onClick={handleArrowBack} />

        {company && <h1 className="company__name">{company}</h1>}
        {position && <h2 className="job__position">{position}</h2>}

        <p>
          {jobLocation && <span>{jobLocation},</span>}
          {"  "}
          {jobType && <span>{jobType}</span>}
        </p>

        {createdAt && (
          <p className="createdAt">Job posted on {dateAndTime(createdAt)}</p>
        )}

        {jobDescription && (
          <div className="section__head">
            <h3>Description</h3>
            <p>{convertNewLinesToBreak(jobDescription)}</p>
          </div>
        )}
        {jobRequirements && (
          <div className="section__head">
            <h3>Job Requirements</h3>
            <p>{convertNewLinesToBreak(jobRequirements)}</p>
          </div>
        )}
        {qualifications && (
          <div className="section__head">
            <h3>Qualifications</h3>
            <p>{convertNewLinesToBreak(qualifications)}</p>
          </div>
        )}
        {interviewProcess && (
          <div className="section__head">
            <h3>Application/Interview Process</h3>
            <p>{convertNewLinesToBreak(interviewProcess)}</p>
          </div>
        )}
        {benefits && (
          <div className="section__head">
            <h3>Benefits</h3>
            <p>{convertNewLinesToBreak(benefits)}</p>
          </div>
        )}

        {(jobEmail || httpsValue(jobUrl)) && (
          <div className="section__head">
            <h3>Application</h3>
            {jobEmail && (
              <div>
                <p>
                  <span>Apply via this email</span>{" "}
                  <LinkBtn
                    linkContent={jobEmail}
                    url={`mailto:${jobEmail}`}
                    // target="_blank"
                    linkBtn="link__content_style"
                  />
                  <span> using the job title as the subject of your email</span>
                </p>
              </div>
            )}
            {httpsValue(jobUrl) && (
              <div>
                <p>
                  <span>Apply via this link</span>{" "}
                  <LinkBtn
                    linkContent="here"
                    url={jobUrl}
                    target="_blank"
                    linkBtn="link__content_style"
                  />
                </p>
              </div>
            )}
          </div>
        )}

        {(httpsValue(socialMedia?.whatsapp) ||
          httpsValue(socialMedia?.telegram)) && (
          <div className="section__head">
            <h3>Our social media platform(s)</h3>
            <p>
              You can join our social media platform(s) for more jobs updates:{" "}
              {httpsValue(socialMedia?.whatsapp) && (
                <LinkBtn
                  linkContent="WhatsApp"
                  url={socialMedia?.whatsapp}
                  target="_blank"
                  linkBtn="link__content_style"
                />
              )}{" "}
              {httpsValue(socialMedia.telegram) && (
                <LinkBtn
                  linkContent="Telegram"
                  url={socialMedia?.telegram}
                  target="_blank"
                  linkBtn="link__content_style"
                />
              )}
              {/* <a href={whatsapp}>WhatsApp</a> <a href={telegram}>Telegram</a> */}
            </p>
          </div>
        )}
      </JobDetailWrapper>
    </AddJobAllJobsProfile>
  );
};

export default SinglePostJobComp;

const LoaderWrapper = styled.div`
  .wide__loader {
    height: 100px;
    width: 100px;
    top: 30%;
  }
  @media screen and (max-width: 650px) {
    .wide__loader {
      right: 45%;
      height: 50px;
      width: 50px;
    }
  }
`;

const JobDetailWrapper = styled.div`
  /* border: 2px solid red; */
  max-width: 1000px;
  margin: 0px auto 2rem;
  padding-inline: 1rem;

  .arrow__back {
    font-size: 25px;
    margin-block: 8px;
    cursor: pointer;
  }
  .company__name {
    font-size: 28px;
  }
  .job__position {
    margin-block: 8px;
    font-size: 20px;
  }

  @media screen and (max-width: 540px) {
    padding-inline: 0;
    margin-block: 8px;
    font-size: 14px;

    .arrow__back {
      font-size: 25px;
      margin-block: 0px;
      cursor: pointer;
    }
    .company__name {
      font-size: 24px;
    }
    .job__position {
      font-size: 18px;
    }
  }

  .createdAt {
    margin-block: 8px;
    font-weight: 600;
  }

  .section__head {
    margin-top: 1.5rem;
  }

  .link__content_style {
    display: inline;
  }
  .link__content_style > a {
    font-size: 16px;
    text-decoration: underline;
    color: var(--dark);
    font-weight: 600;
  }

  @media screen and (max-width: 540px) {
    .createdAt {
      font-size: 14px;
    }

    .section__head > h3 {
      font-size: 16px;
    }
  }
`;
